/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { TFunction } from 'i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormDateTime,
  isXHoursAfter,
  isXHoursBefore,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import { dateUtils } from '@opengeoweb/shared';
import { WARN_NAMESPACE, useWarningsTranslation } from '../../../utils/i18n';

export const VALID_FROM_MAX_HOURS_AFTER_CURRENT = 192;
export const VALID_UNTIL_DEFAULT_OFFSET = 6;

export const isValueBeforeCurrentTime = (
  value: string,
  t: TFunction,
): boolean | string => {
  return (
    isXHoursBefore(value, dateUtils.getCurrentTimeAsString(), 0) ||
    t('warning-valid-from-error-before-current')
  );
};

export const isMaxHoursAfterCurrentTime = (
  value: string,
  t: TFunction,
): boolean | string =>
  isXHoursAfter(
    value,
    dateUtils.getCurrentTimeAsString(),
    VALID_FROM_MAX_HOURS_AFTER_CURRENT,
  ) ||
  t('warning-valid-from-error-max-hours', {
    ns: WARN_NAMESPACE,
    MAX_HOURS: VALID_FROM_MAX_HOURS_AFTER_CURRENT,
  });

/**
 * Automatically adjusts the valid until field when valid from is changed
 * If the new valid from time is after the current valid until time,
 * it sets valid until to 6 hours after valid from
 * @param validFrom - The new valid from ISO date string
 * @param validUntil - The current valid until ISO date string
 * @returns The adjusted valid until ISO date string, or null if no adjustment needed
 */
export const offsetTimeValidUntilField = (
  validFrom: string,
  validUntil: string,
): string | null => {
  const validUntilValue = dateUtils.getUtcFromString(validUntil);
  const valueFrom = dateUtils.getUtcFromString(validFrom);

  if (
    valueFrom &&
    validUntilValue &&
    dateUtils.isAfter(valueFrom, validUntilValue)
  ) {
    return dateUtils
      .add(valueFrom, { hours: VALID_UNTIL_DEFAULT_OFFSET })
      .toISOString();
  }
  return null;
};

export const ValidFrom: React.FC<{
  isDisabled?: boolean;
  isReadOnly?: boolean;
}> = ({ isDisabled, isReadOnly }) => {
  const { t } = useWarningsTranslation();
  const { isRequired } = useDraftFormHelpers();
  const { trigger, getValues, setValue } = useFormContext();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeValidFrom = (value: any): void => {
    if (!value) {
      return;
    }
    const newValidUntilOffset = offsetTimeValidUntilField(
      value,
      getValues('validUntil'),
    );
    if (newValidUntilOffset) {
      setValue('validUntil', newValidUntilOffset, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    // If the new value is after the current valid until, trigger validation
    void trigger(['validFrom', 'validUntil']);
  };

  return (
    <ReactHookFormDateTime
      disablePast
      disabled={isDisabled || isReadOnly}
      isReadOnly={isReadOnly}
      label={t('warning-valid-from')}
      name="validFrom"
      onChange={onChangeValidFrom}
      rules={{
        validate: {
          isRequired,
          isValidDate: dateUtils.isValidIsoDateString,
          // The valid from time cannot be before the current time
          isValueBeforeCurrentTime: (value: string): boolean | string =>
            isValueBeforeCurrentTime(value, t),
          // Valid from can be no more than X hours after current time
          isMaxHoursAfterCurrentTime: (value: string): boolean | string =>
            isMaxHoursAfterCurrentTime(value, t),
        },
      }}
    />
  );
};
